import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { supabase } from '../../lib/supabase';

// Type specific to this component's needs
type OrganizationEditData = { // Renamed type for clarity
  id: string;
  name: string;
  type: string;
  // wallet_balance is no longer needed or fetched here
  monthly_management_fee_rate: number | null;
  filter_expiry_products: boolean;
};

export function EditOrganization() {
  // Use the corrected type for state
  const [organization, setOrganization] = useState<OrganizationEditData | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [monthlyFeeRate, setMonthlyFeeRate] = useState('');
  const [filterExpiryProducts, setFilterExpiryProducts] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Define fetchOrganization with useCallback *before* useEffect
  const fetchOrganization = useCallback(async () => {
    // Check for ID early
    if (!id) {
      setError("Organization ID not found in URL.");
      setLoading(false); // Ensure loading stops if no ID
      return; 
    }
    
    setLoading(true); 
    setError(null);   
    try {
      const { data, error: fetchError } = await supabase
        .from('organizations')
        .select('id, name, type, monthly_management_fee_rate, filter_expiry_products') 
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Organization not found');

      // Set state based on fetched data
      setOrganization(data as OrganizationEditData); 
      setName(data.name);
      setType(data.type);
      setMonthlyFeeRate(data.monthly_management_fee_rate === null || data.monthly_management_fee_rate === undefined ? '' : data.monthly_management_fee_rate.toString());
      setFilterExpiryProducts(data.filter_expiry_products ?? true);

    } catch (err: any) {
      console.error('Error fetching organization:', err);
      setError(err.message);
    } finally {
      setLoading(false); 
    }
  // Add 'id' to useCallback dependency array
  }, [id]); 

  useEffect(() => {
    fetchOrganization();
    // useEffect depends on the stable fetchOrganization reference
  }, [fetchOrganization]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      if (!id) throw new Error('Organization ID is required');

      const rateToSave = monthlyFeeRate.trim() === '' ? null : parseFloat(monthlyFeeRate);
      if (monthlyFeeRate.trim() !== '' && isNaN(rateToSave as number)) {
        throw new Error('Invalid number entered for monthly fee rate.');
      }

      const { error: updateError } = await supabase
        .from('organizations')
        .update({
          name,
          type,
          monthly_management_fee_rate: rateToSave,
          filter_expiry_products: filterExpiryProducts,
        })
        .eq('id', id);

      if (updateError) throw updateError;

      navigate('/dashboard');
    } catch (err: any) {
      console.error('Error updating organization:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setMonthlyFeeRate(value);
    }
  };

  if (!organization && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Edit Organization
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Organization Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              label="Organization Type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              helperText="e.g., Enterprise, SMB"
            />

            <TextField
              margin="normal"
              fullWidth
              type="number"
              label="Monthly Fee Rate per SIM (Optional)"
              value={monthlyFeeRate}
              onChange={handleRateChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">R</InputAdornment>,
                inputProps: { min: 0, step: 0.01 }
              }}
              helperText="Leave blank to use the system default rate."
            />

            <FormControlLabel
              control={
                <Switch
                  checked={filterExpiryProducts}
                  onChange={(e) => setFilterExpiryProducts(e.target.checked)}
                />
              }
              label="Filter Expiry Products"
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              When enabled, the frontend will hide products with expiry dates (1 Day, 7 Day, 30 Day, etc.) from product selection dropdowns.
            </Typography>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                fullWidth
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/dashboard')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
} 